// The unit boundary between Live and Audiotool.
//
// Live speaks BEATS (fractional, 4-decimal precision — see canonical.ts's r4)
// and MIDI velocity on 0–127. Audiotool speaks TICKS (`utils.Ticks.Beat` =
// 3840 to the quarter note) and velocity on 0–1. Everything that crosses the
// bridge in either direction goes through this module, so the conversions are
// pinned here once rather than re-derived at each call site.
//
// Two rules are load-bearing and are the reason this file exists at all:
//
//   1. A DURATION IS THE DIFFERENCE OF ROUNDED ENDPOINTS, never the rounding
//      of a difference. Two clips that abut exactly in Live must still abut in
//      ticks. Rounding each duration independently drifts by up to a tick per
//      clip, which shows up in the studio as a hairline gap or — worse, since
//      Audiotool may reject it — a one-tick overlap. Hence `spanTicks`;
//      `beatsToTicks` is for absolute positions only.
//
//   2. VELOCITY ROUNDS ON THE WAY BACK, NOT ON THE WAY OUT. Quantising the
//      0–1 value would make the round trip lossy (1/127 rounded to 4dp comes
//      back as 1.0033, not 1). Live tolerates fractional velocity —
//      `canonicalNotes` in shared/midi/notes.ts uses r4, not Math.round — so
//      the unit value keeps full precision and only the 0–127 side rounds.
//
// Pure: no I/O, no deps. Lives in shared/ because the mappers that use it are
// tested from workers/ while the code that calls them runs in the extension.

/** Ticks in one quarter note — Audiotool's `utils.Ticks.Beat`. */
export const TICKS_PER_BEAT = 3840;

/** Audiotool's `config.tempoBpm` range. Outside it the field is rejected. */
export const TEMPO_MIN = 30;
export const TEMPO_MAX = 1000;

/** Round to 4 decimals — the sync engine's ASSERT_EPS precision. Duplicated
 * from sync/canonical.ts rather than imported so this module stays leaf, the
 * same trade shared/midi/notes.ts makes. */
function r4(x: number): number {
  return Math.round(x * 1e4) / 1e4;
}

function clamp(x: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, x));
}

/** Beats → ticks. ABSOLUTE POSITIONS ONLY — use `spanTicks` for anything with
 * a length, or neighbouring clips stop lining up. */
export function beatsToTicks(beats: number): number {
  return Math.round(beats * TICKS_PER_BEAT);
}

/** Ticks → beats, at the snapshot's own precision. */
export function ticksToBeats(ticks: number): number {
  return r4(ticks / TICKS_PER_BEAT);
}

/**
 * A clip's span in ticks, from its beat endpoints.
 *
 * The duration is `round(end) - round(start)`, so a chain of clips laid end to
 * end in Live is a chain of regions laid end to end in Audiotool, with no
 * accumulated drift. A non-positive result is returned as-is: a zero- or
 * negative-length clip is the caller's problem to report, not this module's to
 * paper over.
 */
export function spanTicks(
  startBeats: number,
  endBeats: number,
): { positionTicks: number; durationTicks: number } {
  const positionTicks = beatsToTicks(startBeats);
  return { positionTicks, durationTicks: beatsToTicks(endBeats) - positionTicks };
}

/** Live velocity (0–127) → Audiotool velocity (0–1). Deliberately unrounded. */
export function velocityToUnit(v127: number): number {
  return clamp(v127, 0, 127) / 127;
}

/** Audiotool velocity (0–1) → Live velocity (0–127), at Live's precision. */
export function unitToVelocity(unit: number): number {
  return r4(clamp(unit, 0, 1) * 127);
}

/** Fit a Live tempo into Audiotool's range. A non-finite or non-positive
 * tempo counts as clamped — the caller warns, and never writes a NaN. */
export function clampTempo(bpm: number): { bpm: number; clamped: boolean } {
  if (!Number.isFinite(bpm) || bpm <= 0) return { bpm: TEMPO_MIN, clamped: true };
  const clamped = clamp(bpm, TEMPO_MIN, TEMPO_MAX);
  return { bpm: clamped, clamped: clamped !== bpm };
}

/** Round a tick count up to the next bar line. 4/4 gives 15360 — which is
 * both Audiotool's `Region.durationTicks` default and its `Ticks.SemiBreve`. */
export function ceilToBar(ticks: number, numerator: number, denominator: number): number {
  const bar = (TICKS_PER_BEAT * 4 * numerator) / denominator;
  return Math.ceil(ticks / bar) * bar;
}
